import { LegalPageShell } from '@/components/public/legal-page-shell';
import { PRIVACY_POLICY_VERSION } from '@/constants/legal';

/**
 * PrivacyPolicyPage — public, unauthenticated route at `/privacy`.
 *
 * Linked from:
 *  - login.tsx footer ("By signing in you agree to ... Privacy Policy")
 *  - ActivationPage consent step
 *  - ResidentSelfRegistrationPage consent step
 *
 * Content is governed by Kenya Data Protection Act 2019 (KDPA), the Data
 * Protection (General) Regulations 2021, and the Children Act 2022.
 *
 * Reviewed and approved by Secure Labs co-founders, 5 May 2026, in
 * lieu of external counsel sign-off until the first paying estate is
 * onboarded. A counsel review is scheduled before commercial launch and
 * any redlines they propose will be bumped via PRIVACY_POLICY_VERSION.
 *
 * Bump rules:
 *   - Material change (new processor, new purpose, new data category, new
 *     module enabled by default at launch): bump PRIVACY_POLICY_VERSION and
 *     add an entry to the version history table at the foot of the page.
 *   - Typo / clarification: edit in place; do not bump version.
 *
 * Module assumptions for this version (must match `feature-status.md` and
 * the Estate Operator's actual module configuration at first-estate
 * onboarding):
 *   - Always on:    CORE, COMPLIANCE_DPA
 *   - On at launch: RESIDENTIAL, DELIVERY, RECURRING, MAPS, ANNOUNCEMENTS,
 *                   ENTERPRISE_TENANTS
 *   - Off:          PHOTO_ID (no biometric processing), all other modules
 *
 * If module configuration changes, this notice MUST be revised before the
 * change goes live to a property — particularly any toggle of PHOTO_ID,
 * EMERGENCY, ANALYTICS, or PAYMENTS.
 */
export function PrivacyPolicyPage() {
  return (
    <LegalPageShell
      title="Privacy Policy"
      effectiveDate="5 May 2026"
      version={PRIVACY_POLICY_VERSION}
    >
      {/* Plain-language summary — non-binding, sits above the formal notice
          so a reader gets the gist before deciding how deep to read. */}
      <section
        aria-label="Plain-language summary"
        className="rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-5 text-[15px] leading-relaxed"
      >
        <h2 className="text-base font-semibold text-[var(--text-primary)]">Quick summary</h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          A non-binding plain-language overview. Sections 1–20 below are the notice that governs.
        </p>
        <ul className="mt-3 list-disc space-y-1.5 pl-5">
          <li>
            <strong>What we collect:</strong> your name, phone, email, the invitations and visits
            you create, security and access events, and the records we keep to operate the gate.
          </li>
          <li>
            <strong>Why we collect it:</strong> to let your estate manage who comes through the
            gate, to send you the notifications you ask for, and to keep an audit trail.
          </li>
          <li>
            <strong>Who else sees it:</strong> the property manager and guards at your estate, and a
            small list of vendors that help us operate (SMS, email, push, hosting, storage, mapping,
            password-breach checks, and in-app support chat). We do not sell your data and we do not
            share it with advertisers.
          </li>
          <li>
            <strong>How long we keep it:</strong> 90 days for visit data (configurable down to 30 by
            your estate), 1–5 years for security and audit logs depending on the law that requires
            them, and until you ask us to delete it for the rest.
          </li>
          <li>
            <strong>What you can do:</strong> see, correct, download, restrict or delete your data —
            start at <strong>Account → Privacy &amp; Data</strong>. We respond within 30 days as
            required by Kenyan law and usually much sooner.
          </li>
          <li>
            <strong>Who to contact:</strong> our Data Protection Officer George Macharia at{' '}
            <a className="underline" href="mailto:dpo@securelabsystems.com">
              dpo@securelabsystems.com
            </a>{' '}
            or +254 745 424 949.
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl">Contents</h2>
        <ol className="ml-6 list-decimal space-y-1 text-sm">
          <li>
            <a className="hover:underline" href="#privacy-1">
              Definitions
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-2">
              Who we are
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-3">
              Scope of this notice
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-4">
              Personal data we process
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-5">
              Where the data comes from
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-6">
              Lawful basis for processing
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-7">
              How we use your data
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-8">
              Sharing with third parties (sub-processors)
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-9">
              International transfers
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-10">
              Retention
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-11">
              Your rights under the KDPA
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-12">
              Security
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-13">
              Children
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-14">
              Cookies
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-15">
              SMS pre-registration
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-16">
              Enterprise tenants and employees
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-17">
              Breach notification
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-18">
              What happens if you do not agree
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-19">
              Contact
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#privacy-20">
              Version history
            </a>
          </li>
        </ol>
      </section>

      <section id="privacy-1" className="scroll-mt-24">
        <h2 className="text-xl">1. Definitions</h2>
        <p>In this notice:</p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            <strong>Platform</strong> means the Secure Labs visitor and access management software
            provided by Secure Labs Limited.
          </li>
          <li>
            <strong>We</strong>, <strong>us</strong>, <strong>our</strong> refer to Secure Labs
            Limited.
          </li>
          <li>
            <strong>You</strong> means a data subject — typically a resident, employee, visitor,
            guard, property manager, security supervisor, organisation administrator, enterprise
            tenant administrator, or platform super administrator whose data is processed on the
            Platform.
          </li>
          <li>
            <strong>Estate Operator</strong> means the residential estate, commercial property, or
            master-planned development that has subscribed to the Platform and uses it to manage
            access at its premises.
          </li>
          <li>
            <strong>Enterprise Tenant</strong> means a company or organisation that occupies part of
            an Estate Operator&apos;s property and whose administrator manages employee access
            through the Platform under the Estate Operator&apos;s subscription.
          </li>
          <li>
            <strong>Sub-processor</strong> means a third-party service we engage to process personal
            data on our behalf, under a written agreement.
          </li>
          <li>
            <strong>KDPA</strong> means the Kenya Data Protection Act 2019 (Act No. 24 of 2019).
          </li>
          <li>
            <strong>ODPC</strong> means the Office of the Data Protection Commissioner of Kenya.
          </li>
        </ul>
      </section>

      <section id="privacy-2" className="scroll-mt-24">
        <h2 className="text-xl">2. Who we are</h2>
        <p>
          The Platform is operated by Secure Labs Limited, a company incorporated in Kenya, with
          registered office at P.O. Box 8456-00100, Tom Mboya Street, Nairobi, Kenya. Secure Labs
          Limited holds an Office of the Data Protection Commissioner (ODPC) Data Controller
          registration in respect of its own user accounts. Our registration as a Data Processor
          (covering personal data we process on behalf of an Estate Operator) is being filed as a
          separate ODPC submission; we will update this notice and bump its version when that
          registration completes.
        </p>
        <p>
          For most of the Platform&apos;s features, an Estate Operator is the data controller and
          Secure Labs Limited is its data processor. The relationship is governed by a Data
          Processing Agreement between us and the Estate Operator. The Estate Operator&apos;s own
          privacy notice may apply alongside this one.
        </p>
        <p>
          For some activities we act as an <strong>independent data controller</strong>, including:
          operating and securing our own infrastructure, monitoring for abuse and fraud, maintaining
          audit logs, managing our own user accounts and business relationships, and complying with
          legal obligations that apply directly to us (such as tax, accounting, and regulatory
          requirements).
        </p>
        <p>
          Our Data Protection Officer is George Macharia, reachable at{' '}
          <a className="underline" href="mailto:dpo@securelabsystems.com">
            dpo@securelabsystems.com
          </a>{' '}
          or by telephone on +254 745 424 949.
        </p>
      </section>

      <section id="privacy-3" className="scroll-mt-24">
        <h2 className="text-xl">3. Scope of this notice</h2>
        <p>
          This notice describes how we collect, use, share, retain, and protect personal data when
          you interact with the Platform in any of the following capacities:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            You hold an account with us (resident, employee, guard, property manager, security
            supervisor, organisation administrator, enterprise tenant administrator, or platform
            super administrator).
          </li>
          <li>
            You are a visitor who has been invited to or arrived at an Estate Operator&apos;s
            premises and your details have been processed through the Platform.
          </li>
          <li>
            You are a domestic worker, courier, ride-hail driver, or contractor whose access pass is
            held on the Platform.
          </li>
          <li>You contact us directly with a question, complaint, or data-subject request.</li>
        </ul>
        <p>
          Where the Platform is used by an Estate Operator or an enterprise tenant, that
          organisation is an independent data controller for its own employment, tenancy, and
          visitor-management purposes. The Estate Operator or enterprise tenant is responsible for
          having a lawful basis to process personal data through the Platform, providing its own
          privacy notice to its residents, employees, guards, and visitors, and obtaining any
          consents required — including, where relevant, parental or guardian consent for children.
          This notice does not cover processing carried out by Estate Operators or enterprise
          tenants in their capacity as independent data controllers.
        </p>
        <p>
          The Kenya Data Protection Act 2019 is our primary governing law because Secure Labs
          Limited is incorporated in Kenya and processes personal data in Kenya and the SADC region.
          If you access the Platform from outside Kenya, your data will be protected to at least the
          standard set by the KDPA, and we will comply with any additional mandatory local
          requirements that apply.
        </p>
      </section>

      <section id="privacy-4" className="scroll-mt-24">
        <h2 className="text-xl">4. Personal data we process</h2>
        <p>We process the following categories of personal data:</p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            <strong>Account identity data:</strong> full name, email address, mobile phone number,
            residential unit identifier (where applicable), and a confirmation that you are at least
            eighteen (18) years old.
          </li>
          <li>
            <strong>Visitor identity data:</strong> visitor full name, mobile phone number, optional
            email address, optional national identification number, and vehicle registration plate
            (where applicable). Visitor data is provided by the inviting resident, employee, or
            property manager — see §5.
          </li>
          <li>
            <strong>Pass holder data (recurring and worker passes):</strong> name, phone number,
            pass type, access zone or zones, valid-from and valid-until dates, and revocation status
            of recurring passes issued to domestic workers, delivery personnel, couriers, and
            contractors.
          </li>
          <li>
            <strong>Visit and access data:</strong> invitation records, access events (timestamps,
            gates used, entry and exit), pass type, valid-from and valid-until windows,
            accompanying-visitor details, and guard notes on access decisions (for walk-in
            requests).
          </li>
          <li>
            <strong>Authentication data:</strong> password hashes (we never store plain-text
            passwords), multi-factor authentication seeds, session metadata, authentication-event
            logs.
          </li>
          <li>
            <strong>Communication data:</strong> notifications dispatched to you (SMS, email, push
            notification) and their delivery status.
          </li>
          <li>
            <strong>Device and usage data:</strong> IP address, user-agent string, request
            identifiers used for diagnostic, security, and audit purposes.
          </li>
          <li>
            <strong>Consent records:</strong> the version of this notice and the Terms of Service
            that you accepted, the date of acceptance, your IP address at the time, and your browser
            type and version — retained for five (5) years per KDPA §32(4).
          </li>
          <li>
            <strong>Incident documentation:</strong> guard-uploaded photographs and notes attached
            to incident reports (e.g., a damaged barrier, a security event). These are documentary
            photographs of incidents, not biometric data.
          </li>
          <li>
            <strong>Saved contacts and preferences:</strong> your saved visitor shortlist (names and
            access-scheduling preferences), and your notification settings per estate.
          </li>
        </ul>
        <p>
          The Platform supports an optional photo-capture module (PHOTO_ID) that, when enabled by an
          Estate Operator, would allow a visitor selfie to be taken at the gate.{' '}
          <strong>This module is not enabled at launch.</strong> If an Estate Operator enables it in
          future, we will update this notice and re-prompt affected users for explicit consent
          before any photo capture begins, treat those photos as{' '}
          <strong>sensitive personal data</strong> within the meaning of the KDPA, apply stricter
          access controls and shorter retention, and carry out a Data Protection Impact Assessment
          (DPIA) where required.
        </p>
      </section>

      <section id="privacy-5" className="scroll-mt-24">
        <h2 className="text-xl">5. Where the data comes from</h2>
        <p>We collect personal data from the following sources:</p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            <strong>From you directly.</strong> When you register, activate your account, update
            your profile, or contact us, you provide your name, phone number, email address, and
            other information required to operate the Platform.
          </li>
          <li>
            <strong>From the inviting resident or employee.</strong> When a resident or employee
            invites you to visit a property, they enter your name, phone number, and (optionally)
            other details on your behalf. The inviting resident or employee is responsible for
            having a lawful basis to share your details with us.
          </li>
          <li>
            <strong>From the Estate Operator.</strong> When an estate onboards a new resident,
            employee, or guard, the Estate Operator may provide your name, email, phone, and unit
            assignment to the Platform. The Estate Operator is responsible for having a lawful basis
            to share your data with us for access-management purposes and to use it for those
            purposes.
          </li>
          <li>
            <strong>From SMS replies.</strong> When a visitor pre-registers by replying to an SMS
            with their invitation code, we process the reply using the phone number already
            associated with the invitation. See §15.
          </li>
          <li>
            <strong>From your device.</strong> When you use the Platform, your device sends us its
            IP address, user-agent string, and (where permitted) the device token used to deliver
            push notifications.
          </li>
        </ul>
      </section>

      <section id="privacy-6" className="scroll-mt-24">
        <h2 className="text-xl">6. Lawful basis for processing</h2>
        <p>We rely on the following lawful bases under KDPA §30:</p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            <strong>Consent</strong> for: optional marketing communications (where we choose to send
            them in future), age-of-majority confirmation at sign-up, and any optional features
            added in future that materially expand the data we process. Note that service and
            security communications (visit confirmations, access alerts, account and security
            notices) are sent as part of the service and do not require separate consent.
          </li>
          <li>
            <strong>Performance of a contract</strong> for: account provisioning, authentication,
            visitor invitation processing, pass management, and other operational features you
            actively use.
          </li>
          <li>
            <strong>Legal obligation</strong> for: tax records, audit-log retention, breach
            notification to the ODPC, and responses to lawful requests by Kenyan authorities.
          </li>
          <li>
            <strong>Legitimate interest</strong> (balanced against your rights) for: security
            incident detection, retention of access events for a defined window after a visit,
            anti-fraud, and product reliability monitoring.
          </li>
        </ul>
        <p>
          In more detail: access control and audit trails rely on performance of contract and
          legitimate interests; security monitoring, anti-fraud, and incident documentation rely on
          legitimate interests balanced against your rights; device and usage data is processed on
          the basis of legitimate interests for Platform reliability and security. National
          identification numbers, where provided, are processed only where necessary for identity
          verification or security purposes, on the basis of legitimate interests and (where
          applicable) legal obligation, and are stored with the same technical protections as other
          identity documents.
        </p>
        <p>
          We have assessed that our legitimate interests in Platform security, fraud prevention, and
          service reliability are not overridden by your interests or fundamental rights, given the
          limited scope of data used for these purposes and the access controls and retention limits
          applied to it.
        </p>
      </section>

      <section id="privacy-7" className="scroll-mt-24">
        <h2 className="text-xl">7. How we use your data</h2>
        <p>
          We process personal data to (i) provide and maintain the Platform, (ii) operate access
          control at participating Estate Operators, (iii) issue and validate visitor QR passes,
          (iv) dispatch notifications to you and to your invited visitors, (v) investigate and
          respond to security incidents, (vi) comply with our regulatory and contractual
          obligations, and (vii) improve the safety and reliability of the Platform.
        </p>
        <p>
          QR validation at the gate is an automated process — when a guard scans a visitor&apos;s QR
          code, the Platform decides automatically whether to display a green &ldquo;valid&rdquo; or
          red &ldquo;invalid&rdquo; result. This decision does not produce legal effects within the
          meaning of KDPA §35 and the final decision to admit a visitor remains with the guard. We
          do not engage in any other solely automated decision-making and we do not profile you.
        </p>
        <p>
          Service and security communications — including visit confirmations, access alerts,
          account notifications, and security advisories — are sent as part of the service and do
          not require separate consent; you can only stop receiving them by closing your account.
          Optional marketing communications, if we introduce them, will require your prior consent
          and will carry a clear opt-out in every message.
        </p>
        <p>
          Data relating to one Estate Operator or enterprise tenant is not accessible to another,
          except where required by law or at your explicit instruction.
        </p>
      </section>

      <section id="privacy-8" className="scroll-mt-24">
        <h2 className="text-xl">8. Sharing with third parties (sub-processors)</h2>
        <p>
          We share personal data only with sub-processors that act on our written instructions under
          a Data Processing Agreement, or where we are legally compelled to disclose. We do not sell
          personal data, and we do not share it with advertising networks or data brokers. We
          require each sub-processor to: (i) not engage additional sub-processors without our prior
          written approval and equivalent contractual protections; and (ii) implement appropriate
          technical and organisational security measures.
        </p>
        <p className="mt-2 font-medium">Currently active sub-processors:</p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            <strong>Africa&apos;s Talking</strong> (Kenya) — SMS dispatch and two-way SMS handling
            (recipient phone number and message body).
          </li>
          <li>
            <strong>Resend</strong> (United States) — transactional email dispatch (recipient email
            address and message body).
          </li>
          <li>
            <strong>Firebase Cloud Messaging (Google LLC)</strong> (United States) — delivery of
            push notifications to your device (device token only).
          </li>
          <li>
            <strong>Amazon Web Services (AWS)</strong> (South Africa — af-south-1, Cape Town) —
            managed application hosting, the relational database, the message broker, and related
            compute/networking infrastructure.
          </li>
          <li>
            <strong>Cloudflare, Inc.</strong> (United States, with regional storage in af-south-1) —
            DNS, web application firewall, content delivery, and the R2 object store we use for
            visitor pass attachments, DSAR export packages, and incident photographs.
          </li>
          <li>
            <strong>Google Maps (Google LLC)</strong> (United States) — embedded map and directions
            on the visitor invitation page so the visitor can navigate to the gate. Your IP address
            and the gate co-ordinates are visible to Google when the embed loads.
          </li>
          <li>
            <strong>Have I Been Pwned (HIBP)</strong> (United Kingdom) — at password set or reset,
            we check the first five characters of the SHA-1 hash of your password against the HIBP
            service to detect known-breached passwords. We never send your password or its full
            hash; HIBP cannot identify you from the partial hash.
          </li>
          <li>
            <strong>Intercom, Inc.</strong> (United States) — in-app support chat and AI-assisted
            help (Fin AI agent). When you open the support chat, we share your name and email
            address with Intercom so that our support team and Fin can identify you and access your
            conversation history. We do not share your phone number, national ID, unit details, or
            visitor data with Intercom. Conversation content you submit through the chat is stored
            on Intercom&apos;s servers. Intercom&apos;s own privacy policy is available at{' '}
            <a
              className="underline"
              href="https://www.intercom.com/legal/privacy"
              target="_blank"
              rel="noreferrer"
            >
              intercom.com/legal/privacy
            </a>
            .
          </li>
        </ul>
        <p className="mt-2 font-medium">Planned sub-processors (not yet active):</p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            <strong>Sentry</strong> — error monitoring (we will configure server-side PII filtering
            before any production traffic reaches it).
          </li>
          <li>
            <strong>Grafana Labs</strong> — operational telemetry and traces (no personal data will
            be shipped to Grafana under our logging policy).
          </li>
        </ul>
        <p>
          We will update this notice and bump its version before any planned sub-processor begins
          processing your data.
        </p>
      </section>

      <section id="privacy-9" className="scroll-mt-24">
        <h2 className="text-xl">9. International transfers</h2>
        <p>
          We make commercially reasonable efforts to keep all personal data within Kenya or the SADC
          region (our primary hosting region is AWS af-south-1, Cape Town, and our object storage on
          Cloudflare R2 is configured for the same region). Where a sub-processor processes data
          outside this region, we use appropriate contractual safeguards and technical measures
          recognised under Kenyan data protection law — including data transfer agreements
          incorporating standard protective clauses, and pseudonymisation or aggregation where
          feasible. Where required by the ODPC, we will register such transfers and reflect any
          changes in this notice.
        </p>
        <p>The specific transfers you should be aware of:</p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            <strong>Africa&apos;s Talking (Kenya, with international SMS routing).</strong>{' '}
            Africa&apos;s Talking is a Kenyan company, but SMS messages are routed internationally
            through mobile network operators. The data transferred is the recipient&apos;s phone
            number and message body.
          </li>
          <li>
            <strong>Cloudflare control plane (United States).</strong> While our R2 object store is
            configured to keep data in the af-south-1 region, Cloudflare&apos;s edge network and
            account management are operated from the United States. Account-level metadata (request
            logs, security events) may be processed in the United States.
          </li>
          <li>
            <strong>Resend (United States).</strong> Transactional email is dispatched via
            Resend&apos;s infrastructure in the United States. The data transferred is your email
            address and the body of the email we send you.
          </li>
          <li>
            <strong>Google (Maps and Firebase Cloud Messaging).</strong> When the visitor invitation
            page renders the gate map, your browser fetches map tiles directly from Google&apos;s
            servers in the United States. When you receive a push notification, the message is
            dispatched via Google&apos;s servers.
          </li>
          <li>
            <strong>Have I Been Pwned (HIBP).</strong> The k-anonymity hash check described in §8 is
            sent to HIBP&apos;s servers in the United Kingdom. The partial hash sent does not
            identify you.
          </li>
          <li>
            <strong>Intercom, Inc. (United States — AWS us-east-1).</strong> When you use the in-app
            support chat, your name and email address are transmitted to Intercom&apos;s servers
            hosted in the United States. Conversation content you submit is stored there. Intercom
            processes this data under its standard Data Processing Agreement, which is incorporated
            into its Terms of Service. We rely on Intercom&apos;s standard contractual protections
            as the safeguard for this transfer.
          </li>
        </ul>
      </section>

      <section id="privacy-10" className="scroll-mt-24">
        <h2 className="text-xl">10. Retention</h2>
        <p>
          We retain personal data only for as long as is necessary for the purpose for which it was
          collected. In this section, &ldquo;anonymised&rdquo; means irreversibly de-identified such
          that we can no longer link the data to any natural person — not even with additional
          information held separately. Where we retain any identifier alongside stripped data (for
          example, for incident investigation), that data is pseudonymised and remains subject to
          these retention periods and technical protections.
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Visitor personal data: 90 days from visit completion (configurable per Estate Operator
            down to a 30-day floor); afterwards anonymised.
          </li>
          <li>
            Access events: 2 years after the visit, retained for security audit. Personal data on
            the event is anonymised after the visitor-data retention window above.
          </li>
          <li>Authentication and security events: 1 year.</li>
          <li>Notification delivery records: 90 days.</li>
          <li>Audit logs (administrative): 3 years.</li>
          <li>Audit logs (DSAR / consent / data-subject events): 5 years (KDPA requirement).</li>
          <li>Consent records: 5 years (KDPA §32(4)).</li>
          <li>Incident photographs uploaded by a guard: 90 days from upload.</li>
          <li>DSAR export packages: 30 days from compilation.</li>
          <li>Invitation lifecycle records (creation through completion): 2 years.</li>
          <li>Recurring and worker pass holder data: 90 days after pass expiry or revocation.</li>
          <li>Cookies: see §14 for the per-cookie expiry.</li>
        </ul>
        <p>
          Deleted data may remain in encrypted backups for up to 7 days, after which it is
          overwritten on the regular backup cycle and is not used for active processing. We may
          retain certain records for longer than the periods above where required by law or to
          establish, exercise, or defend legal claims.
        </p>
      </section>

      <section id="privacy-11" className="scroll-mt-24">
        <h2 className="text-xl">11. Your rights under the KDPA</h2>
        <p>
          As a data subject in Kenya you have the following rights, exercisable at any time and free
          of charge:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>The right to be informed about how your data is processed (this notice).</li>
          <li>
            The right of access — for account holders, request a copy of your data via Account
            Settings → Privacy → Request My Data.
          </li>
          <li>
            The right to rectification — correct inaccurate data via your account profile, or by
            emailing us at{' '}
            <a className="underline" href="mailto:privacy@securelabsystems.com">
              privacy@securelabsystems.com
            </a>
            .
          </li>
          <li>
            The right to erasure — request deletion of your data, subject to limited exemptions
            where retention is required by law (for example, audit-log obligations).
          </li>
          <li>The right to restriction of processing.</li>
          <li>
            The right to data portability — receive your data in a structured, machine-readable JSON
            format.
          </li>
          <li>The right to object to processing based on legitimate interest.</li>
          <li>The right to withdraw consent at any time, where consent is the lawful basis.</li>
          <li>
            The right not to be subject to a decision based solely on automated processing that
            significantly affects you (KDPA §35) — we carry out no such processing; see §7.
          </li>
          <li>
            The right to lodge a complaint with the ODPC at{' '}
            <a className="underline" href="mailto:info@odpc.go.ke">
              info@odpc.go.ke
            </a>
            .
          </li>
        </ul>
        <p>
          Self-service rights of access, rectification, and erasure are available in the account
          dashboard. Restriction requests are also submitted via the dashboard but are fulfilled by
          our DPO within the statutory 30-day window — they are not automatically applied at the
          time of submission. The remaining rights (portability, objection, withdrawal of consent
          for non-essential processing) are exercised today by writing to{' '}
          <a className="underline" href="mailto:privacy@securelabsystems.com">
            privacy@securelabsystems.com
          </a>
          ; a self-service interface for these is on our roadmap. We respond to data-subject
          requests within thirty (30) days as required by KDPA §26 (a one-time extension of up to a
          further 30 days may apply to complex requests, with notice to you), and we typically
          respond materially faster as an internal operational target.
        </p>
        <p>
          Before fulfilling a request, we may need to verify your identity by asking for information
          we reasonably require. We may refuse or limit a request where the KDPA permits — for
          example, where fulfilment would adversely affect the rights of another person, where we
          are subject to a conflicting legal obligation, or where a request is manifestly unfounded
          or excessive. Where we refuse, we will give you the reason in writing.
        </p>
        <p>
          Visitors who do not hold an account can exercise these rights by contacting the Estate
          Operator they visited or by emailing us directly at{' '}
          <a className="underline" href="mailto:privacy@securelabsystems.com">
            privacy@securelabsystems.com
          </a>
          .
        </p>

        <h3 className="mt-4 text-base font-semibold">
          11.1 How to lodge a complaint with the ODPC
        </h3>
        <p>
          If you believe we have processed your personal data in breach of the Kenya Data Protection
          Act 2019 — and you are not satisfied with our response under §11 above — you have the
          right to lodge a complaint with the Office of the Data Protection Commissioner (ODPC). The
          ODPC is the independent regulator established under §5 of the Act, and the procedure is
          governed by the Data Protection (Complaints Handling and Enforcement) Regulations 2021.
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            <strong>Online complaints portal:</strong>{' '}
            <a
              className="underline"
              href="https://complaints.odpc.go.ke"
              target="_blank"
              rel="noreferrer"
            >
              complaints.odpc.go.ke
            </a>{' '}
            (the recommended route — submission generates a tracking reference).
          </li>
          <li>
            <strong>Email:</strong>{' '}
            <a className="underline" href="mailto:info@odpc.go.ke">
              info@odpc.go.ke
            </a>
            , quoting your name, the subject of the complaint, and the Secure Labs reference number
            (if any) we previously gave you.
          </li>
          <li>
            <strong>Postal address:</strong> Office of the Data Protection Commissioner, P.O. Box
            5588-00100, Nairobi, Kenya.
          </li>
          <li>
            <strong>Telephone:</strong> +254 20 222 2331 (general enquiries).
          </li>
        </ul>
        <p>
          Lodging a complaint with the ODPC is <strong>free of charge</strong>. The ODPC is required
          by Regulation 9 to acknowledge your complaint within seven (7) days, to issue a
          preliminary determination within ninety (90) days, and to give you a written outcome at
          the close of its investigation. You are not required to attempt resolution with us first,
          although doing so often resolves the issue faster.
        </p>
        <p>
          Sanctions the ODPC can impose under §63 of the Act and §13 of the Enforcement Regulations
          include compliance orders, monetary penalties up to KES 5,000,000 or one per cent (1%) of
          our annual turnover (whichever is lower), and orders for compensation payable to you under
          §65.
        </p>
      </section>

      <section id="privacy-12" className="scroll-mt-24">
        <h2 className="text-xl">12. Security</h2>
        <p>
          In plain terms: we encrypt your sensitive data, restrict who inside the company can see
          what, keep an unalterable record of administrative actions, and require strong passwords
          with optional multi-factor authentication.
        </p>
        <p>
          In technical terms: personal data fields are encrypted at rest using industry-standard
          encryption (currently AES-256-GCM). Data in transit is encrypted using TLS (v1.2 or
          higher). Tenant isolation is enforced by PostgreSQL row-level security at the database
          layer and by role-based access controls in the application layer. Authentication uses
          strong password hashing (currently Argon2id) with HttpOnly, Secure, SameSite=Strict
          session cookies. We maintain an immutable, append-only audit log for all administrative
          actions and data-subject events.
        </p>
        <p>
          For higher-risk processing activities — such as any future biometric or photo-ID module,
          or any systematic monitoring feature — we will carry out a Data Protection Impact
          Assessment in line with KDPA requirements and ODPC guidance before enabling that
          processing.
        </p>
      </section>

      <section id="privacy-13" className="scroll-mt-24">
        <h2 className="text-xl">13. Children</h2>
        <p>
          The Platform is intended for use by adults (eighteen years and older). We do not knowingly
          collect personal data from children under eighteen. Account registration requires
          confirmation that you are at least eighteen years of age.
        </p>
        <p>
          In some cases, information about children may be entered into the Platform by a parent,
          guardian, employer, or Estate Operator (for example, a minor visitor accompanying an
          adult, or a child mentioned in an incident report). Where this happens, we process that
          information only for the limited purpose of managing access at the property and
          documenting security incidents. We do not use children&apos;s data for marketing or
          profiling. Where information about a child is entered by a parent, guardian, employer, or
          Estate Operator, that person or organisation is responsible for obtaining any consent
          required under the KDPA and the Children Act 2022 before entering the child&apos;s
          information.
        </p>
        <p>
          Rights under the KDPA in respect of a child&apos;s personal data may be exercised by the
          child, their parent, or guardian, in accordance with the Children Act 2022.
        </p>
        <p>
          If you believe we hold personal data of a child in a way that is inconsistent with this
          section, please contact us at{' '}
          <a className="underline" href="mailto:privacy@securelabsystems.com">
            privacy@securelabsystems.com
          </a>{' '}
          and we will investigate and delete or appropriately restrict the data without undue delay,
          taking into account the best interests of the child and the requirements of the Children
          Act 2022 and the KDPA.
        </p>
      </section>

      <section id="privacy-14" className="scroll-mt-24">
        <h2 className="text-xl">14. Cookies</h2>
        <p>
          We use only strictly necessary cookies — session and authentication cookies required for
          the Platform to function. We do not use advertising or analytics cookies that require
          consent.
        </p>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--border-default)]">
              <th className="py-2 text-left">Cookie name</th>
              <th className="py-2 text-left">Purpose</th>
              <th className="py-2 text-left">Lifetime</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-2">
                <code>access_token</code>
              </td>
              <td className="py-2">
                Short-lived JWT used to authenticate API calls during a session.
              </td>
              <td className="py-2">15 minutes</td>
            </tr>
            <tr>
              <td className="py-2">
                <code>refresh_token</code>
              </td>
              <td className="py-2">
                Used to mint a new access token without re-entering your password.
              </td>
              <td className="py-2">7 days maximum, unless you log out or revoke it sooner</td>
            </tr>
            <tr>
              <td className="py-2">
                <code>mfa_pending</code>
              </td>
              <td className="py-2">Holds the in-progress multi-factor authentication challenge.</td>
              <td className="py-2">5 minutes</td>
            </tr>
            <tr>
              <td className="py-2">
                <code>impersonation_token</code>
              </td>
              <td className="py-2">
                Set only when a platform super administrator is acting on your behalf for support
                purposes; every such session is audit-logged and you are notified by email.
              </td>
              <td className="py-2">
                Maximum 2 hours; revoked immediately when the super-admin ends the support session
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          All four cookies are set with the HttpOnly, Secure, and SameSite=Strict attributes. They
          cannot be read by JavaScript and they are not sent on cross-site requests.
        </p>
        <p>
          Third-party services integrated into the Platform — in particular Cloudflare (CDN / WAF)
          and Google Maps (map embed) — may set their own cookies or collect device-level
          information when their content loads. These are governed by their respective privacy
          notices; we do not use them for advertising or profiling, and they are not under our
          control.
        </p>
        <p>
          We do not use browser localStorage or sessionStorage to store personal data. All
          authentication state is held in the HttpOnly cookies listed above. Any localStorage keys
          we use hold UI preferences only and contain no personal data.
        </p>
      </section>

      <section id="privacy-15" className="scroll-mt-24">
        <h2 className="text-xl">15. SMS pre-registration</h2>
        <p>
          Some Estate Operators allow a visitor to pre-register for entry by replying to an SMS with
          their six-digit invitation code. When you do this:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Your phone number is already known to us from the invitation your host created.</li>
          <li>
            You reply with your six-digit code to the Secure Labs shortcode (keyword: SECURELABS).
          </li>
          <li>
            If the code matches a valid invitation, we activate your visit and send you a PIN for
            gate entry.
          </li>
        </ul>
        <p>
          The lawful basis for processing your data through the SMS channel is the contract between
          Secure Labs Limited and the Estate Operator and your inviting host&apos;s assertion of
          lawful basis to share your details with us (see Terms of Service §6). SMS messages are
          handled on our behalf by Africa&apos;s Talking and/or Vonage (see §8). The SMS record is
          retained for the same period as the linked visit (see §10). If you would rather not
          register by SMS, the visitor invitation page (the link in the SMS your host sent you)
          provides the same flow over a regular web browser.
        </p>
      </section>

      <section id="privacy-16" className="scroll-mt-24">
        <h2 className="text-xl">16. Enterprise tenants and employees</h2>
        <p>
          Where an Estate Operator hosts an enterprise tenant (a company occupying part of the
          property), the company&apos;s designated administrator can:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Onboard employees as users of the Platform under the company&apos;s account.</li>
          <li>
            View visitor invitations and access events scoped to the company&apos;s zone of the
            property.
          </li>
          <li>Manage worker passes for the company&apos;s domestic and contract staff.</li>
        </ul>
        <p>
          If you are an employee of an enterprise tenant, your employer is the data controller for
          visitor and access data within the company&apos;s zone, and we act as the company&apos;s
          data processor. Your employer&apos;s own privacy policy may apply alongside this notice in
          respect of your employment data. Your employer is responsible for having a lawful basis to
          onboard you as a Platform user and to share your employment data with us.
        </p>
      </section>

      <section id="privacy-17" className="scroll-mt-24">
        <h2 className="text-xl">17. Breach notification</h2>
        <p>
          In the event of a personal-data breach that is likely to result in risk to your rights and
          freedoms, we notify the ODPC within seventy-two (72) hours of becoming aware of the breach
          (KDPA §43), and we notify affected data subjects without undue delay.
        </p>
        <p>
          Where we notify you of a breach, we will do so by email to the address on your account or,
          if that is not possible, by in-app notice. The notification will describe the nature of
          the breach, the categories of personal data affected, the likely consequences, and the
          steps we are taking to address it, in line with KDPA §43.
        </p>
      </section>

      <section id="privacy-18" className="scroll-mt-24">
        <h2 className="text-xl">18. What happens if you do not agree</h2>
        <p>
          If you do not accept this notice, you cannot use the Platform. You may close this page
          without creating an account. If you have already created an account and you do not wish to
          accept a new version of this notice when we publish one, you can ask us to delete your
          account and any partial data we have collected by emailing{' '}
          <a className="underline" href="mailto:privacy@securelabsystems.com">
            privacy@securelabsystems.com
          </a>
          . We will action the request within thirty (30) days of confirming your identity, subject
          to any data we must retain to comply with legal obligations or to establish, exercise, or
          defend legal claims (see §10).
        </p>
      </section>

      <section id="privacy-19" className="scroll-mt-24">
        <h2 className="text-xl">19. Contact</h2>
        <p>
          <strong>Secure Labs Limited</strong>
          <br />
          P.O. Box 8456-00100, Tom Mboya Street, Nairobi, Kenya
          <br />
          Telephone: +254 745 424 949
        </p>
        <p>
          Data Protection Officer: George Macharia
          <br />
          DPO email:{' '}
          <a className="underline" href="mailto:dpo@securelabsystems.com">
            dpo@securelabsystems.com
          </a>
          <br />
          General privacy enquiries:{' '}
          <a className="underline" href="mailto:privacy@securelabsystems.com">
            privacy@securelabsystems.com
          </a>
        </p>
        <p>
          If you are not satisfied with our response, you have the right to lodge a complaint with
          the ODPC — see §11.1 for full contact details and the complaints procedure.
        </p>
      </section>

      <section id="privacy-20" className="scroll-mt-24">
        <h2 className="text-xl">20. Version history</h2>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--border-default)]">
              <th className="py-2 text-left">Version</th>
              <th className="py-2 text-left">Effective date</th>
              <th className="py-2 text-left">Summary of changes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-2">{PRIVACY_POLICY_VERSION}</td>
              <td className="py-2">5 May 2026</td>
              <td className="py-2">First public version.</td>
            </tr>
          </tbody>
        </table>
      </section>
    </LegalPageShell>
  );
}
